import { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { WbCard, WbGrid, WbGridCol, WbGridRow, WbHeading } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import {
	_getEquipmentSpecification,
	_getSpesification,
} from '../../../Utils/Vimdt/apiCalls';
import _notification from '../../../Utils/Vimdt/notification';
import _handleErrors from '../../../Utils/Vimdt/handleErrors';
import useFilter from '../../../Utils/Vimdt/useFilter';
import { marketList } from '../../../Constants/Vimdt/marketList';
import VimdtProductKey from '../Common/VimdtProductKey';
import { useImageContext } from '../../../Contexts/ImageContext';

import ExampleSpesificationData from '../../../Assets/Vimdt/ExampleSpesification/example_specification_vehicles.json';
import ExampleEmissionData from '../../../Assets/Vimdt/ExampleSpesification/example_specification_emissions.json';
import ExampleEquipmentData from '../../../Assets/Vimdt/ExampleSpesification/example_specification_equipments.json';
import ExampleMeServiceData from '../../../Assets/Vimdt/ExampleSpesification/example_specification_meservice.json';

import VimdtButton from '../Common/VimdtButton';
import VimdtInput from '../Common/VimdtInput';
import VimdtSelect from '../Common/VimdtSelect';
import VimdtSwitch from '../Common/VimdtSwitch';
import VimdtAccordion from '../Common/VimdtAccordion';
import PanelHeader from '../Common/PanelHeader';
import SpecificationLabel from '../Common/SpecificationLabel';
import CopyButton from '../Common/CopyButton';
import CollapseAllToggle from '../Common/CollapseAllToggle';
import JsonWithSearchAndSave from './JsonWithSearchAndSave';
import './Specification.scss';

const TAB_KEY = 'specification';

function Specification() {
	const { t } = useTranslation();
	const { tabApikeys, setTabApikey } = useImageContext();

	const [apikey, setApikey] = useState(() => tabApikeys[TAB_KEY] || null);
	const [vin, setVin] = useState(null);
	const [marketId, setMarketId] = useState('de_DE');

	const [specificationBtn, setSpecificationBtn] = useState(false);
	const [equipmentsBtn, setEquipmentsBtn] = useState(true);
	const [additionalSpecsBtn, setAdditionalSpecsBtn] = useState(false);
	const [optionsNullDescriptionBtn, setOptionsNullDescriptionBtn] = useState(true);
	const [optionsBtn, setOptionsBtn] = useState(false);
	const [technicalDataBtn, setTechnicalDataBtn] = useState(false);
	const [payloadNullValuesBtn, setPayloadNullValuesBtn] = useState(true);

	const [spesificationData, setSpesificationData] = useState(null);
	const [isImagesLoading, setIsImagesLoading] = useState(false);
	const [fileName, setFileName] = useState('');
	const [isApiKeyData, setIsApiKeyData] = useState(false);
	const [prevVin, setPrevVin] = useState(null);
	const [nullFilter, setNullFilter] = useState(false);
	const [filterInput, setFilterInput] = useState('');
	const [panels, setPanels] = useState([]);
	const [settingsPanels, setSettingsPanels] = useState([]);
	const [availablePanels, setAvailablePanels] = useState([]);

	const notifyKey = useRef();

	useEffect(() => {
		setTabApikey(TAB_KEY, apikey);
	}, [apikey, setTabApikey]);
	const renderSpesificationData = useFilter(spesificationData, filterInput, nullFilter);

	const openNotification = (title, message, key) => {
		notifyKey.current = _notification({
			title,
			message,
			key: key || notifyKey.current,
			onClose: () => {
				notifyKey.current = undefined;
			},
		});
	};

	useEffect(() => {
		if (!spesificationData) return;
		const categories = [];
		const hasData = (section) =>
			spesificationData[section] && Object.keys(spesificationData[section]).length > 0;
		if (hasData('specifications')) categories.push('1');
		if (hasData('emissions')) categories.push('2');
		if (hasData('equipments')) categories.push('3');
		if (hasData('meservice')) categories.push('4');
		setAvailablePanels(categories);
	}, [spesificationData]);

	const specificationsSettingsDataTable = [
		{
			key: 1,
			type: t('vimdt.specificationsTab.specificationsTabName'),
			desc: t('vimdt.specificationsTab.infoTable.contentSettings.specificationsDesc'),
		},
		{
			key: 3,
			type: t('vimdt.specificationsTab.equipmentsTabName'),
			desc: t('vimdt.specificationsTab.infoTable.contentSettings.equipmentsDesc'),
		},
		{
			key: 7,
			type: t('vimdt.components.selectMarket'),
			desc: t('vimdt.specificationsTab.infoTable.contentSettings.selectMarket'),
		},
	];

	const specificationsParametersDataTable = [
		{
			key: 1,
			type: t('vimdt.specificationsTab.specificationsParametersLabels.additionalSpecs'),
			desc: t('vimdt.specificationsTab.infoTable.specificationsParameters.additionalSpecsDesc'),
		},
		{
			key: 2,
			type: t('vimdt.specificationsTab.specificationsParametersLabels.optionsNullDescription'),
			desc: t(
				'vimdt.specificationsTab.infoTable.specificationsParameters.optionsNullDescriptionDesc'
			),
		},
		{
			key: 3,
			type: t('vimdt.specificationsTab.specificationsParametersLabels.options'),
			desc: t('vimdt.specificationsTab.infoTable.specificationsParameters.optionsDesc'),
		},
		{
			key: 4,
			type: t('vimdt.specificationsTab.specificationsParametersLabels.technicalData'),
			desc: t('vimdt.specificationsTab.infoTable.specificationsParameters.technicalDataDesc'),
		},
		{
			key: 5,
			type: t('vimdt.specificationsTab.specificationsParametersLabels.payloadNullValues'),
			desc: t('vimdt.specificationsTab.infoTable.specificationsParameters.payloadNullValuesDesc'),
		},
	];

	const getSpesification = async () => {
		const params = { marketId, vin, apikey };
		// The switches are labelled on/off, so the query flags are inverted.
		const specificationQuery = {
			additionalSpecs: !additionalSpecsBtn,
			optionsNullDescription: !optionsNullDescriptionBtn,
			options: !optionsBtn,
			technicalData: !technicalDataBtn,
			payloadNullValues: !payloadNullValuesBtn,
		};

		if (prevVin !== vin) {
			setSpesificationData(null);
			setPrevVin(vin);
		}

		setIsImagesLoading(true);

		try {
			const specificationResponse =
				!specificationBtn && (await _getSpesification({ ...params, ...specificationQuery }));
			const equipmentsResponse =
				!equipmentsBtn && (await _getEquipmentSpecification(params));

			const specData = {
				specifications:
					specificationResponse?.data &&
					specificationResponse.status === 200 &&
					specificationResponse.data.statusCode !== 404
						? specificationResponse.data
						: null,
				equipments:
					equipmentsResponse?.data &&
					equipmentsResponse.status === 200 &&
					equipmentsResponse.data.statusCode !== 404
						? equipmentsResponse.data
						: null,
			};

			if (specificationResponse?.status === 404) {
				openNotification(
					t('vimdt.notification.error'),
					`${t('vimdt.notification.vehiclesError')}${vin}\n`,
					'load_with_specification_error_notification'
				);
			}
			if (equipmentsResponse?.status === 204) {
				openNotification(
					t('vimdt.notification.error'),
					`${t('vimdt.notification.equipmentError')}${vin}\n`,
					'load_with_equipment_error_notification'
				);
			}

			setIsImagesLoading(false);

			if (specData.specifications || specData.equipments) {
				openNotification(
					t('vimdt.notification.finished'),
					`${t('vimdt.notification.specificationLoaded')} ${vin}.`,
					'load_notification'
				);
				setSpesificationData(specData);
				setFileName(vin);
				setIsApiKeyData(true);
			}
		} catch (error) {
			setIsImagesLoading(false);
			openNotification(t('vimdt.notification.error'), _handleErrors(error, vin));
		}
	};

	const getSampleSpesification = () => {
		setIsApiKeyData(false);
		setSpesificationData({
			specifications: ExampleSpesificationData,
			emissions: ExampleEmissionData,
			equipments: ExampleEquipmentData,
			meservice: ExampleMeServiceData,
		});
		openNotification(
			t('vimdt.notification.finished'),
			`${t('vimdt.notification.specificationLoaded')} ${t('vimdt.notification.sample')}.`,
			'load_notification'
		);
	};

	const saveSpesification = () => {
		const zip = new JSZip();

		Object.keys(spesificationData).forEach((key) => {
			const value = spesificationData[key];
			if (value === null || value === 'No data found') return;
			zip.file(`${fileName}_${key}.json`, JSON.stringify(value, null, 2));
		});

		zip.generateAsync({ type: 'blob' }).then((content) => {
			saveAs(content, `${fileName}_specification.zip`);
		});
	};

	const panelHandler = (mode) => setPanels(mode === 0 ? availablePanels : []);

	const section = (key, labelKey, dataKey) =>
		spesificationData?.[dataKey] && {
			key,
			header: t(`vimdt.specificationsTab.${labelKey}`),
			content: (
				<>
					{renderSpesificationData && <CopyButton data={renderSpesificationData[dataKey]} />}
					<SpecificationLabel data={renderSpesificationData?.[dataKey]} />
				</>
			),
		};

	const jsonItems = [
		section('1', 'specificationsTabName', 'specifications'),
		section('3', 'equipmentsTabName', 'equipments'),
	].filter(Boolean);

	const contentSettings = (
		<WbGrid fullWidth>
			<WbGridRow className="vimdt-specification__switches">
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSwitch
						label={t('vimdt.specificationsTab.specificationsTabName')}
						onBtn={t('vimdt.components.onButton')}
						offBtn={t('vimdt.components.offButton')}
						status={[t('vimdt.components.onButton'), t('vimdt.components.offButton')]}
						getValue={setSpecificationBtn}
						defaultValue={specificationBtn}
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSwitch
						label={t('vimdt.specificationsTab.equipmentsTabName')}
						onBtn={t('vimdt.components.onButton')}
						offBtn={t('vimdt.components.offButton')}
						status={[t('vimdt.components.onButton'), t('vimdt.components.offButton')]}
						getValue={setEquipmentsBtn}
						defaultValue={equipmentsBtn}
					/>
				</WbGridCol>
				<WbGridCol mq1={12}>
					<VimdtSelect
						label={t('vimdt.components.selectMarket')}
						value={marketId}
						setValue={setMarketId}
						adjustableWith
						options={marketList.map((market) => ({
							id: market.market_code,
							value: market.market_code,
							name: market.market_name,
						}))}
					/>
				</WbGridCol>
			</WbGridRow>
		</WbGrid>
	);

	const parameterSwitches = [
		['additionalSpecs', setAdditionalSpecsBtn, additionalSpecsBtn],
		['optionsNullDescription', setOptionsNullDescriptionBtn, optionsNullDescriptionBtn],
		['options', setOptionsBtn, optionsBtn],
		['technicalData', setTechnicalDataBtn, technicalDataBtn],
		['payloadNullValues', setPayloadNullValuesBtn, payloadNullValuesBtn],
	];

	const parameterSettings = (
		<WbGrid fullWidth>
			<WbGridRow className="vimdt-specification__switches">
				{parameterSwitches.map(([labelKey, setter, value]) => (
					<WbGridCol key={labelKey} mq1={12} mq3={6} mq5={4}>
						<VimdtSwitch
							label={t(`vimdt.specificationsTab.specificationsParametersLabels.${labelKey}`)}
							onBtn={t('vimdt.components.onButton')}
							offBtn={t('vimdt.components.offButton')}
							status={[t('vimdt.components.onButton'), t('vimdt.components.offButton')]}
							getValue={setter}
							defaultValue={value}
						/>
					</WbGridCol>
				))}
			</WbGridRow>
		</WbGrid>
	);

	const saveButton = isApiKeyData && (
		<VimdtButton
			label={t('vimdt.loadSaveButtons.saveSpecs')}
			filledIn={false}
			onClick={saveSpesification}
		/>
	);

	return (
		<div className="vimdt-specification">
			<WbCard className="shared-tab-content-padding">
				<WbHeading size="m">{t('vimdt.specificationsTab.downloadText')}</WbHeading>

				<WbGrid fullWidth>
					<WbGridRow className="vimdt-specification__inputs">
						<WbGridCol mq1={12} mq3={6}>
							<VimdtInput
								label={t('vimdt.placeholders.apiKeyPlaceholder')}
								val={apikey}
								getValue={setApikey}
								password
								testId="vimdt-specification-apikey"
							/>
						</WbGridCol>
						<WbGridCol mq1={12} mq3={6}>
							<VimdtInput
								label={t('vimdt.placeholders.vinNumberPlaceholder')}
								getValue={setVin}
								testId="vimdt-specification-vin"
							/>
						</WbGridCol>
					</WbGridRow>
				</WbGrid>

				<VimdtAccordion
					activeKeys={settingsPanels}
					onChange={setSettingsPanels}
					items={[
						{
							key: 'content-settings',
							header: (
								<PanelHeader
									headerName={t('vimdt.specificationsTab.specificationsContentSettings')}
									description={t('vimdt.specificationsTab.specificationsContentSettingsDesc')}
									data={specificationsSettingsDataTable}
								/>
							),
							content: contentSettings,
						},
						{
							key: 'parameters',
							header: (
								<PanelHeader
									headerName={t('vimdt.specificationsTab.specificationsParameters')}
									description={t('vimdt.specificationsTab.specificationsParametersDesc')}
									data={specificationsParametersDataTable}
								/>
							),
							content: parameterSettings,
						},
					]}
				/>

				<div className="vimdt-specification__actions">
					<VimdtButton
						label={t('vimdt.loadSaveButtons.loadSampleSpecs')}
						onClick={getSampleSpesification}
						disabled={isImagesLoading}
					/>
					<VimdtButton
						label={t('vimdt.loadSaveButtons.loadSpecs')}
						onClick={getSpesification}
						loading={isImagesLoading}
						disabled={!apikey || !vin || (specificationBtn && equipmentsBtn)}
					/>
				</div>
			</WbCard>

			<VimdtProductKey apikey={apikey} />

			{spesificationData && (
				<>
					<div className="vimdt-specification__actions">{saveButton}</div>

					<CollapseAllToggle
						panelHandler={panelHandler}
						panels={panels}
						availablePanels={availablePanels}
					/>

					<JsonWithSearchAndSave
						title={t('vimdt.specificationsTab.filterSpecs')}
						collapse={[panels, setPanels]}
						filterInput={[filterInput, setFilterInput]}
						nullFilter={[nullFilter, setNullFilter]}
						items={jsonItems}
					/>

					<div className="vimdt-specification__footer">{saveButton}</div>
				</>
			)}
		</div>
	);
}

export default Specification;
